import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowUpRight } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { memo } from 'react';
import { ButtonHTMLAttributes, useMemo } from 'react';
import BetaBadgeV2 from 'src/shared/components/ui/badges/beta-badge-v2';
import DynamicIcon, { DynamicIconProps } from 'src/shared/components/ui/icons/dynamic-icon';
import ConditionalParent from 'src/shared/components/utils/ConditionalParent';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import { useMe, useMutateUser } from 'src/shared/queries/useMe';
import mixpanel from 'src/shared/lib/mixpanel';
import { getPathVariable } from 'src/shared/lib/query.utils';
import { cn } from 'src/shared/lib/utils';

const buttonVariants = cva(
  'inline-flex gap-2 text-sm truncate cursor-pointer leading-none w-full items-center hover:bg-neutral-100 text-neutral-950 rounded-sm focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 border border-transparent rounded-sm',
  {
    variants: {
      variant: {
        main: 'px-3 h-9 font-normal data-[expanded=false]:p-0 data-[expanded=false]:w-9 data-[expanded=false]:shrink-0 data-[expanded=false]:justify-center',
        secondary: `h-8 py-1.5 px-2 data-[expanded=false]:text-typography-grey
          data-[expanded=false]:hover:text-typography-black data-[expanded=false]:shrink-0`,
      },
    },
    defaultVariants: {
      variant: 'main',
    },
  }
);

export interface SubItem {
  text: string;
  href: string;
  bookmark?: boolean;
}

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    WithTestId {
  children: string;
  icon?: DynamicIconProps['name'];
  href: string;
  badge?: 'beta' | 'new';
  disabled?: boolean;
  highlight?: string;
  expanded: boolean;
  highlightBannerText?: string;
  active: boolean;
}

function SideNavButtonBase({
  icon,
  children,
  href,
  variant,
  badge,
  disabled,
  expanded,
  testId,
  active,
  ...props
}: Omit<ButtonProps, 'highlight' | 'highlightBannerText'>) {
  const external = !href.startsWith('/');

  return (
    <div data-testid={`sidenav-button-${children.toLowerCase()}`} className="relative select-none">
      <ConditionalParent
        condition={!expanded}
        parent={(_children) => (
          <WithTooltip side="right" tooltip={children}>
            {_children}
          </WithTooltip>
        )}
      >
        {external ? (
          <a className="relative peer" href={href} target="_blank" rel="noreferrer noopener">
            <button
              data-active={active}
              data-expanded={expanded}
              className={cn(
                buttonVariants({ variant }),
                active && 'border-neutral-200 bg-neutral-100 !text-typography-black'
              )}
              data-testid={testId}
              {...props}
            >
              {icon && <DynamicIcon name={icon} size={16} className="shrink-0" />}
              {expanded && (
                <>
                  {children}
                  <ArrowUpRight size={16} className="stroke-neutral-400" />
                  {badge && <BetaBadgeV2 text={badge} />}
                </>
              )}
            </button>
          </a>
        ) : (
          <Link
            className="relative peer"
            to={href}
            onClick={(e) => {
              disabled && e.preventDefault();
            }}
          >
            <button
              data-active={active}
              data-expanded={expanded}
              className={cn(
                buttonVariants({ variant }),
                active && 'border-neutral-200 bg-neutral-100 !text-typography-black'
              )}
              data-testid={testId}
              {...props}
            >
              {icon && <DynamicIcon name={icon} size={16} className="shrink-0" />}
              {expanded && (
                <>
                  {children}
                  {badge && <BetaBadgeV2 text={badge} />}
                </>
              )}
            </button>
          </Link>
        )}
      </ConditionalParent>
    </div>
  );
}

function SideNavButtonHighlighted({
  icon,
  children,
  href,
  variant,
  badge,
  disabled,
  highlight,
  expanded,
  testId,
  highlightBannerText,
  active,
  ...props
}: ButtonProps) {
  const { data: user } = useMe('settings');
  const { updateUser } = useMutateUser();

  const external = !href.startsWith('/');
  const isHighlighted =
    highlight &&
    user?.settings &&
    highlight in user.settings &&
    user.settings[highlight as keyof typeof user.settings] === undefined;

  function handleHighlight() {
    if (isHighlighted) {
      updateUser.mutate({ settings: { [highlight as string]: true } });
    }
  }

  return (
    <div data-testid={`sidenav-button-${children.toLowerCase()}`} className="relative select-none">
      {!active && isHighlighted && (
        <div className="absolute -top-[1px] -left-[1px] w-[calc(100%+2px)] h-[calc(100%+2px)] peer-hover:bg-neutral-200 rounded-sm border border-dashboardblue animate-ping-border" />
      )}
      {expanded && isHighlighted && highlightBannerText && (
        <div className="mb-1 p-2 text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded-md">
          {highlightBannerText}
        </div>
      )}
      <ConditionalParent
        condition={!expanded}
        parent={(_children) => (
          <WithTooltip side="right" tooltip={children}>
            {_children}
          </WithTooltip>
        )}
      >
        <Link
          className="relative peer"
          to={href}
          target={external ? '_blank' : undefined}
          onClick={(e) => {
            disabled && e.preventDefault();
            handleHighlight();
          }}
        >
          <button
            data-active={active}
            data-expanded={expanded}
            className={cn(
              buttonVariants({ variant }),
              active && !external && 'border-neutral-200 bg-neutral-100 !text-typography-black'
            )}
            data-testid={testId}
            {...props}
          >
            {icon && <DynamicIcon name={icon} size={16} className="shrink-0" />}
            {expanded && (
              <>
                {children}
                {external && <ArrowUpRight size={16} className="stroke-neutral-400" />}
                {badge && <BetaBadgeV2 text={badge} />}
              </>
            )}
          </button>
        </Link>
      </ConditionalParent>
    </div>
  );
}

function SideNavButton(props: ButtonProps) {
  if (props.highlight) {
    return <SideNavButtonHighlighted {...props} />;
  }
  const { ...rest } = props;
  return <SideNavButtonBase {...rest} />;
}

interface ChildNavButtonProps {
  parentHref: string;
  children: SubItem[];
}

export function ChildNavButton({ parentHref, children }: ChildNavButtonProps) {
  const { pathname: pathName } = useLocation();

  children.sort((a, b) => (a.bookmark ? (b.bookmark ? 0 : -1) : b.bookmark ? 1 : 0));

  const unlistedSubItem = useMemo(() => {
    if (children.length <= 3) {
      return false;
    }
    const parentActive = pathName.includes(getPathVariable(parentHref, 0));
    const activeSubItemPath = getPathVariable(pathName, 1);
    if (!activeSubItemPath) {
      return false;
    }
    const listedItems = children.slice(0, 3).map((subItem) => subItem.href.replace('/', ''));
    const notListed = !listedItems.includes(activeSubItemPath);

    return parentActive && notListed;
  }, [children, parentHref, pathName]);

  return (
    <div>
      {children.slice(0, 3).map((subItem, index) => {
        const active = getPathVariable(pathName, 1) === subItem.href.replace('/', '');

        return (
          <Link
            onClick={() =>
              mixpanel.track('Navigation Sub Item Clicked', {
                subItem: subItem.text,
              })
            }
            key={index}
            to={parentHref + subItem.href}
          >
            <div className="h-8 group/subitem flex items-center cursor-pointer pr-4">
              <div className="ml-1.5 w-7 flex-center h-full">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="h-full">
                  <line x1="50%" y1="-10%" x2="50%" y2="32%" strokeWidth="1.2" className="stroke-neutral-200" />
                  {index !== 2 && children.length > index + 1 ? (
                    <line x1="50%" y1="68%" x2="50%" y2="110%" strokeWidth="1.2" className="stroke-neutral-200" />
                  ) : (
                    unlistedSubItem && (
                      <line x1="50%" y1="68%" x2="50%" y2="110%" strokeWidth="1.2" className="stroke-neutral-200" />
                    )
                  )}

                  <circle
                    cx="50%"
                    cy="50%"
                    r="2.7"
                    className={cn(
                      active ? 'fill-black' : 'group-hover/subitem:fill-black fill-neutral-200 transition-colors'
                    )}
                  />
                </svg>
              </div>
              <p
                className={cn(
                  'truncate text-[13px]  font-light capitalize text-typography-grey group-hover/subitem:text-typography-black transition-colors',
                  active && 'text-typography-black'
                )}
              >
                {subItem.text}
              </p>
            </div>
          </Link>
        );
      })}
      {unlistedSubItem && (
        <Link to={pathName}>
          <div className="h-8 group flex items-center cursor-pointer pr-4">
            <div className="ml-1.5 w-7 flex-center h-full">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="h-full">
                <line
                  x1="50%"
                  y1="-10%"
                  x2="50%"
                  y2="32%"
                  strokeWidth="1.2"
                  className="stroke-neutral-200"
                  strokeDasharray="2,2"
                />

                <circle cx="50%" cy="50%" r="2.7" className="fill-black" />
              </svg>
            </div>
            <p className="truncate text-[13px] font-light capitalize text-typography-black">
              {children.find((subItem) => subItem.href === `/${getPathVariable(pathName, 1)}`)?.text}
            </p>
          </div>
        </Link>
      )}
      {children.length > 3 && (
        <Link to={parentHref}>
          <div className="h-5 group/more flex items-center cursor-pointer">
            <p className="pl-[17px] group-hover/more:underline truncate text-[13px] text-typography-grey font-light group-hover/more:text-typography-black transition-colors">
              View {children.length - 3} more
            </p>
          </div>
        </Link>
      )}
    </div>
  );
}

function areSideNavButtonPropsEqual(prev: ButtonProps, next: ButtonProps) {
  return (
    prev.active === next.active &&
    prev.expanded === next.expanded &&
    prev.href === next.href &&
    prev.icon === next.icon &&
    prev.variant === next.variant &&
    prev.disabled === next.disabled &&
    prev.badge === next.badge &&
    prev.children === next.children &&
    prev.highlight === next.highlight &&
    prev.highlightBannerText === next.highlightBannerText &&
    prev.testId === next.testId
  );
}

const MemoSideNavButton = memo(SideNavButton, areSideNavButtonPropsEqual);

export default MemoSideNavButton;
export { MemoSideNavButton as SideNavButton, buttonVariants };
